import { Injectable } from '@angular/core';

/**
 * Service for managing service units (vehicles) data transformation
 * Handles conversion between array and object formats, filtering, and validation
 * 
 * Category: Business Logic Service
 * Purpose: Units data transformation, filtering, and structure management
 */
@Injectable()
export class ServiceUnitsManagerService {

  constructor() { }

  /**
   * Filters units array to only include units with quantity > 0
   * @param unidades - Array of units with cantidad property
   * @returns Filtered array of units
   */
  filterUnidadesSinCantidad(unidades: any[]): any[] {
    if (!unidades) {
      return [];
    }
    return unidades.filter(unidad => unidad.cantidad > 0);
  }

  /**
   * Converts units array to object format for Firebase
   * @param unidades - Array of units [{tipo: string, cantidad: number}]
   * @returns Object with unit types as keys and quantities as values
   */
  convertUnitsArrayToObject(unidades: any[]): any {
    const unitsObject = {};
    
    if (!unidades || unidades.length === 0) {
      return unitsObject;
    }

    unidades.forEach(unidad => {
      if (unidad.tipo && unidad.cantidad !== undefined) {
        unitsObject[unidad.tipo] = unidad.cantidad / 1; // Convert to number
      }
    });

    return unitsObject;
  }

  /**
   * Converts units object to array format
   * @param unidadesObject - Object with unit types as keys
   * @returns Array of units [{tipo: string, cantidad: number}]
   */
  convertUnitsObjectToArray(unidadesObject: any): any[] {
    const unitsArray = [];
    
    if (!unidadesObject) {
      return unitsArray;
    }

    for (const tipo in unidadesObject) {
      if (unidadesObject.hasOwnProperty(tipo)) {
        unitsArray.push({
          tipo: tipo,
          cantidad: unidadesObject[tipo]
        });
      }
    }

    return unitsArray;
  }

  /**
   * Calculates total number of units
   * @param unidades - Array of units
   * @returns Total count of all units
   */
  calculateTotalUnits(unidades: any[]): number {
    if (!unidades || unidades.length === 0) {
      return 0;
    }

    return unidades.reduce((total, unidad) => {
      return total + (unidad.cantidad / 1);
    }, 0);
  }

  /**
   * Ensures all camionetas from punto origen are represented in units array
   * Adds missing unit types with cantidad 0
   * @param unidades - Current units array
   * @param camionetasFromOrigen - Available camionetas from punto origen
   * @returns Updated units array with all unit types
   */
  ensureAllUnitTypesPresent(unidades: any[], camionetasFromOrigen: any[]): any[] {
    if (!camionetasFromOrigen || camionetasFromOrigen.length === 0) {
      return unidades;
    }

    const updatedUnidades = [...unidades];

    camionetasFromOrigen.forEach(camioneta => {
      const exists = updatedUnidades.some(unidad => unidad.tipo === camioneta.nombre);
      
      if (!exists) {
        updatedUnidades.push({
          tipo: camioneta.nombre,
          cantidad: 0
        });
      }
    });

    return updatedUnidades;
  }

  /**
   * Validates if units have been modified
   * @param originalUnits - Original units array
   * @param currentUnits - Current units array
   * @returns True if units have changed
   */
  hasUnitsChanged(originalUnits: any[], currentUnits: any[]): boolean {
    if (!originalUnits || !currentUnits) {
      return false;
    }

    if (originalUnits.length !== currentUnits.length) {
      return true;
    }

    return JSON.stringify(originalUnits) !== JSON.stringify(currentUnits);
  }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/business-logic/service-units-manager.service.ts